import type {
  MapConfig,
  VisibleCitiesSyncState,
} from '@ag2-weathermaker/features/source-map-container/types/source-map.types';
import { Options } from '@angular-slider/ngx-slider';
import {
  DataRegion,
  ForecastMode,
  Parameters,
  PeriodValues,
  SubseasonalPeriod,
  SubseasonalView,
  TempUnits,
  ToastOutlet,
} from './enums';
import { ExportColumnMap, WorkingForecastExportRow } from './forecast-export.types';
import { HeaderSettings } from './header.types';
import {
  NormalTypes,
  PageViews,
  PolygonState,
  PolygonType,
  SocialNetworkLink,
  SubseasonalPeriodConfig,
} from './main-content.types';
import { ToastOutletPositionConfig } from './toast.types';
import { CalendarWeekPeriod, ForecastPeriod, GraphicsPeriod } from './publishing-center.types';

export const MIN_ZOOM = 2;
export const MAX_ZOOM = 10;
export const defaultPageView: PageViews = 'maps';
export const MAIN_MAP_ID = 'mainMap';
export const ERROR_SENTINEL_VALUE = -999;
export const ERROR_CELL_BACKGROUND_COLOR = '#F2F2F2';
export const ERROR_CELL_FONT_COLOR = '#000000';
export const POLYGON_HANDLE_COLOR = '#000000' as `#${string}`;
export const EARTH_EQUATOR_METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392; // Earth's circumference / 256 pixels
export const CLICK_TOLERANCE_PX = 5;
export const MAX_FORECAST_SOURCES = 8;
export const SECONDS_PER_DAY = 86400;
export const MAX_Z_ORDER_VALUE = 99999;
export const DAYS_IN_FORECAST = 15;
export const FORECAST_LABEL_PRIMARY_UPPERCASE = 'PRIMARY';
export const CHUNK_LOAD_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;
export const RELOAD_ATTEMPT_KEY = 'chunk-reload-attempt';
export const RELOAD_RETRY_WINDOW_MS = 10_000;
export const FALLBACK_ELEMENT_ID = 'chunk-load-fallback';

export const DEFAULT_VISIBLE_CITIES_SYNC_STATE: VisibleCitiesSyncState = {
  leaderMapId: MAIN_MAP_ID,
  visibleCityIds: [],
  revision: 0,
};

export const DEFAULT_HEADER: HeaderSettings = {
  tempUnit: TempUnits.Fahrenheit,
  decimalsEnabled: false,
};

export const DEFAULT_NORMAL_TYPES: NormalTypes = '10';

export const TOAST_OUTLET_POSITION_CONFIG: Record<ToastOutlet, ToastOutletPositionConfig> = {
  [ToastOutlet.Global]: {
    rightPx: 25,
    bottomPx: 55,
  },
  [ToastOutlet.MainMap]: {
    rightPx: 15,
    bottomPx: 25,
  },
};

export const OrderedForecastSourceMap: Record<string, string> = {
  ECMWF_AIFS_OP_0P25: 'AIFS',
  ECMWF_AIFS_ENS: 'AIFS ENS',
  ECMWF_OP: 'ECMWF OP',
  ECMWF_ENS: 'ECMWF ENS',
  GFS_OP: 'GFS OP',
  GFS_ENS: 'GFS ENS',
};

export const WorkingForecastColumns: Record<Parameters, string[]> = {
  [Parameters.MIN_TEMP]: ['min_temp', 'edited_min_temp_value_f', 'weighted_min_temp'],
  [Parameters.AVG_TEMP]: ['avg_temp', 'edited_avg_temp_value_f', 'weighted_avg_temp'],
  [Parameters.MAX_TEMP]: ['max_temp', 'edited_max_temp_value_f', 'weighted_max_temp'],
};

export const SourceForecastColumns: Record<Parameters, string[]> = {
  [Parameters.MIN_TEMP]: ['min_temp'],
  [Parameters.AVG_TEMP]: ['avg_temp'],
  [Parameters.MAX_TEMP]: ['max_temp'],
};

export const ParameterMap: Record<string, string> = {
  avg: 'Average',
  min: 'Minimum',
  max: 'Maximum',
};

export const TEMP_OPTIONS = [
  { value: TempUnits.Fahrenheit, label: 'Fahrenheit (°F)' },
  { value: TempUnits.Celsius, label: 'Celsius (°C)' },
];

export const options: Options = {
  floor: 1,
  ceil: 15,
  showTicks: true,
  showTicksValues: false,
  hideLimitLabels: true,

  getLegend: (value: number): string => {
    return `${value}`;
  },
};

// weighting values 0-100% (e.g., {value:100, label: '100%', value: 95, label: '95%'...}
export const WEIGHTING_DROPDOWN_VALUES = Array.from({ length: 21 }, (_, i) => {
  const value = 100 - i * 5;
  return { value, label: `${value}%` };
});

export const WorkingForecastExportColumnMap: ExportColumnMap<keyof WorkingForecastExportRow>[] = [
  { key: 'display_name', header: 'Location' },
  { key: 'site_id', header: 'Site ID' },
  { key: 'init_time', header: 'Forecast Export Date' },
  { key: 'valid_start', header: 'Forecast Valid Start Date' },
  { key: 'valid_end', header: 'Forecast Valid End Date' },
  { key: 'fcst_period_label', header: 'Forecast Period' },
  { key: 'max_temp', header: 'Forecast Max Temp' },
  { key: 'max_temp_norm_30', header: 'Max Temp Normal (30 yr. avg)' },
  { key: 'max_temp_dfn_30', header: 'Max Temp DFN (30 yr. avg)' },
  { key: 'max_temp_norm_10', header: 'Max Temp Normal (10 yr. avg)' },
  { key: 'max_temp_dfn_10', header: 'Max Temp DFN (10 yr. avg)' },
  { key: 'min_temp', header: 'Forecast Min Temp' },
  { key: 'min_temp_norm_30', header: 'Min Temp Normal (30 yr. avg)' },
  { key: 'min_temp_dfn_30', header: 'Min Temp DFN (30 yr. avg)' },
  { key: 'min_temp_norm_10', header: 'Min Temp Normal (10 yr. avg)' },
  { key: 'min_temp_dfn_10', header: 'Min Temp DFN (10 yr. avg)' },

  { key: 'temp_unit', header: 'Unit (C/F)' },
];

export const POLYGON_COLORS: Record<PolygonType | PolygonState, string> = {
  weighted: '#000000',
  manualEdit: '#90CE8D',
  error: '#FF292A',
  errorSelected: '#FF7F7F',
  hover: '#000000',
  active: '#2D69B2',
};

export const POLYGON_LINE_WIDTH = {
  active: 3,
  inactive: 3,
  hover: 5,
  invisible: 0,
} as const;

export const POLYGON_TYPE = {
  WEIGHTED: 'weighted' as PolygonType,
  MANUAL_EDIT: 'manualEdit' as PolygonType,
} as const;

export const POLYGON_PROPERTY_KEY = {
  MID_POINT: 'midPoint',
  SELECTION_POINT: 'selectionPoint',
  TYPE: 'polygonType',
} as const;

export const TEMP_LIMITS_BY_UNIT = {
  [TempUnits.Fahrenheit]: { MIN: -55, MAX: 130 },
  [TempUnits.Celsius]: { MIN: -48, MAX: 55 },
} as const;

export const SHORT_PARAMETER_DISPLAY_NAMES: Record<Parameters, string> = {
  [Parameters.MAX_TEMP]: 'Max Temp',
  [Parameters.MIN_TEMP]: 'Min Temp',
  [Parameters.AVG_TEMP]: 'Avg Temp',
};

export const MAIN_MAP_ICONS: Record<string, string> = {
  ['error-icon']: '/assets/logos/error.png',
  ['temp-edited-arrow-down']: '/assets/logos/temp_edited_arrow_down.svg',
  ['temp-edited-arrow-up']: '/assets/logos/temp_edited_arrow_up.svg',
  ['temp-edited-no-arrow']: '/assets/logos/temp_edited_no_arrow.svg',
};

export const EDITED_PROPERTY_BY_PARAM: Record<Parameters, string> = {
  [Parameters.MAX_TEMP]: 'isMaxEdited',
  [Parameters.MIN_TEMP]: 'isMinEdited',
  [Parameters.AVG_TEMP]: 'isAvgEdited',
};

export const MAP_CENTER_BASED_ON_REGION: Record<DataRegion, [number, number]> = {
  [DataRegion.NA]: [-100, 37],
  [DataRegion.EUR]: [30.86, 56.82],
};

// Region order (sortOrderId) can only ever be ascending — reversing it isn't meaningful.
export const SORT_ORDER_ID_FIELD = 'sortOrderId';

export const CALENDAR_WEEK_PREFIX = 'cw_';

export const buildCalendarWeekKey = (startDateEpoch: number, endDateEpoch: number): string =>
  `${CALENDAR_WEEK_PREFIX}${startDateEpoch}-${endDateEpoch}`;
export const DEFAULT_MAP_CONFIG: MapConfig = {
  id: MAIN_MAP_ID,
  center: MAP_CENTER_BASED_ON_REGION[DataRegion.NA],
  zoom: 3,
};

export const TWO_WEEKS_IN_SECONDS = 14 * SECONDS_PER_DAY;

export const TimeRangeOptions = [
  { value: PeriodValues['1-5day'], label: '1-5 Day', canEdit: true, firstDay: 1, lastDay: 5 },
  { value: PeriodValues['6-10day'], label: '6-10 Day', canEdit: true, firstDay: 6, lastDay: 10 },
  { value: PeriodValues['11-15day'], label: '11-15 Day', canEdit: true, firstDay: 11, lastDay: 15 },
  { value: PeriodValues['1-7day'], label: '1-7 Day', canEdit: true, firstDay: 1, lastDay: 7 },
  { value: PeriodValues['8-14day'], label: '8-14 Day', canEdit: true, firstDay: 8, lastDay: 14 },
];

export const ForecastModeToggleValues = [
  { value: ForecastMode.ShortRange, label: '1 to 15 Days' },
  { value: ForecastMode.Subseasonal, label: 'Subseasonal' },
];

export const FORECAST_MODE_LABEL: Record<ForecastMode, string> = {
  [ForecastMode.ShortRange]: '1 to 15 Day',
  [ForecastMode.Subseasonal]: 'Subseasonal',
};

export const SubseasonalViewToggleValues = [
  { value: SubseasonalView.Daily, label: 'Daily' },
  { value: SubseasonalView.Weekly, label: 'Weekly' },
  { value: SubseasonalView.Period, label: 'Period' },
];

export const SUBSEASONAL_VIEW_PERIODS: Partial<Record<SubseasonalView, SubseasonalPeriod[]>> = {
  [SubseasonalView.Weekly]: [
    SubseasonalPeriod.WEEK_3,
    SubseasonalPeriod.WEEK_4,
    SubseasonalPeriod.WEEK_5,
    SubseasonalPeriod.WEEK_6,
  ],
  [SubseasonalView.Period]: [
    SubseasonalPeriod.WEEK_3_4,
    SubseasonalPeriod.WEEK_5_6,
    SubseasonalPeriod.WEEK_3_TO_6,
    SubseasonalPeriod.DAY_16_20,
  ],
};

export const SUBSEASONAL_PERIODS: Record<SubseasonalPeriod, SubseasonalPeriodConfig> = {
  [SubseasonalPeriod.WEEK_3]: { label: 'Week 3', weekSpan: [3, 3] },
  [SubseasonalPeriod.WEEK_4]: { label: 'Week 4', weekSpan: [4, 4] },
  [SubseasonalPeriod.WEEK_5]: { label: 'Week 5', weekSpan: [5, 5] },
  [SubseasonalPeriod.WEEK_6]: { label: 'Week 6', weekSpan: [6, 6] },
  [SubseasonalPeriod.WEEK_3_4]: { label: 'Week 3-4', weekSpan: [3, 4] },
  [SubseasonalPeriod.WEEK_5_6]: { label: 'Week 5-6', weekSpan: [5, 6] },
  [SubseasonalPeriod.WEEK_3_TO_6]: { label: 'Week 3 to 6', weekSpan: [3, 6] },
  [SubseasonalPeriod.DAY_16_20]: { label: '16 to 20 day', daySpan: [16, 20] },
};

export const SUBSEASONAL_DAILY_DAYS = 46;

export const TempDfnOptions = [
  { value: 'temp', label: 'Temperature' },
  { value: 'dfn', label: 'DFN' },
];

export const NormalValues = [
  { value: '30', label: '30yr Avg Normals' },
  { value: '10', label: '10yr Avg Normals' },
];

export const MapTable = [
  { value: 'maps', label: 'Maps', icon: 'map.svg' },
  { value: 'table', label: 'Table', icon: 'table.svg' },
];

export const DayParameterValues = [
  { value: 'parameter', label: 'Parameter' },
  { value: 'day', label: 'Day' },
];

export const PeriodParameterValues = [
  { value: 'parameter', label: 'Parameter' },
  { value: 'period', label: 'Period' },
];

export const ParametersDropdownValues = [
  { value: 'min' as Parameters, label: 'Minimum Temp' },
  { value: 'max' as Parameters, label: 'Maximum Temp' },
  { value: 'avg' as Parameters, label: 'Average Temp' },
];

export const DailyPeriodValues = [
  { value: 'daily', label: 'Daily' },
  { value: 'period', label: 'Period' },
];

export const ReleaseNotesValues = [
  { value: 'contactUs', label: 'Contact Us' },
  { value: 'releaseNotes', label: 'Release Notes' },
];

export const ReleaseNotesValuesOpenFromLoad = [{ value: 'releaseNotes', label: 'Release Notes' }];

export const RELEASE_NOTES_TOAST_MESSAGE =
  'Release notes are available to view any time under the question mark in the header.';

export const NO_LOCATIONS_IN_BOUNDING_BOX_MESSAGE = 'There were no locations found within the bounding box drawn.';

export const CONTACT_US_SOCIAL_NETWORKS_LINKS: SocialNetworkLink[] = [
  {
    href: 'https://www.linkedin.com/company/atmosphericg2/',
    label: 'Visit LinkedIn profile',
    icon: 'linkedin',
    tooltip: 'LinkedIn',
  },
  {
    href: 'https://twitter.com/Atmospheric_G2',
    label: 'Visit Twitter profile',
    icon: 'twitter-filled',
    tooltip: 'Twitter',
  },
];

export const CALENDAR_WEEK_PERIODS: CalendarWeekPeriod[] = [
  CalendarWeekPeriod.CALENDAR_WEEK_1,
  CalendarWeekPeriod.CALENDAR_WEEK_2,
  CalendarWeekPeriod.CALENDAR_WEEK_3,
];

export const PERIOD_LABEL_TO_GRAPHICS_PERIOD: Record<string, GraphicsPeriod> = {
  'Days 1-5': ForecastPeriod.DAYS_1_5,
  'Days 6-10': ForecastPeriod.DAYS_6_10,
  'Days 11-15': ForecastPeriod.DAYS_11_15,
  'Days 1-7': ForecastPeriod.DAYS_1_7,
  'Days 8-14': ForecastPeriod.DAYS_8_14,
  [CalendarWeekPeriod.CALENDAR_WEEK_1]: CalendarWeekPeriod.CALENDAR_WEEK_1,
  [CalendarWeekPeriod.CALENDAR_WEEK_2]: CalendarWeekPeriod.CALENDAR_WEEK_2,
  [CalendarWeekPeriod.CALENDAR_WEEK_3]: CalendarWeekPeriod.CALENDAR_WEEK_3,
};
